import React from "react";
import * as Sentry from "@sentry/react";

/**
 * App-wide error boundary (T39 P0 #1/#2 — R29–R34).
 *
 * Catches any render / lifecycle throw below it, reports it to GlitchTip via
 * @sentry/react, and renders a branded fallback instead of React's default
 * blank white screen (one bad API response would otherwise kill a buyer demo).
 *
 * The fallback deliberately uses NO MUI / theme / app context and only inline
 * styles, so it still renders even when the failure is in the theme provider,
 * the redux store, or a shared layout. This is the one place inline styles are
 * the correct call — the crash UI cannot depend on the stylesheet having loaded.
 *
 * Reusable below the top level too: wrap a route/module with <ErrorBoundary>
 * to isolate its blast radius (optional `fallback` render-prop overrides the UI).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // captureException no-ops safely when Sentry has no DSN (lab / dev).
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info && info.componentStack } },
    });
    this.setState({ eventId });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") {
      return fallback({
        error: this.state.error,
        eventId: this.state.eventId,
        reset: this.handleReload,
      });
    }

    const isDev = process.env.NODE_ENV !== "production";
    const { error, eventId } = this.state;

    return (
      <div role="alert" style={S.wrap}>
        <div style={S.card}>
          <div style={S.badge}>OneVision</div>
          <h1 style={S.h1}>Something went wrong</h1>
          <p style={S.p}>
            This page hit an unexpected error. Your data is safe — try reloading,
            or head back to the dashboard.
          </p>
          <div style={S.actions}>
            <button type="button" onClick={this.handleReload} style={S.primaryBtn}>
              Reload page
            </button>
            <button type="button" onClick={this.handleHome} style={S.secondaryBtn}>
              Back to dashboard
            </button>
          </div>
          {eventId ? (
            <p style={S.ref}>
              Error reference: <code style={S.code}>{eventId}</code>
            </p>
          ) : null}
          {isDev && error ? (
            <details style={S.details}>
              <summary style={S.summary}>Developer details</summary>
              <pre style={S.pre}>{String((error && error.stack) || error)}</pre>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}

// Self-contained styles — no theme dependency (see component doc above).
const BRAND = "#1B3F8F";
const S = {
  wrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background: "#f4f6f8",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: "440px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 10px 40px rgba(16,24,40,0.12)",
    padding: "36px 32px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  badge: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND,
    background: "rgba(27,63,143,0.08)",
    padding: "4px 10px",
    borderRadius: "999px",
    marginBottom: "18px",
  },
  h1: { margin: "0 0 10px", fontSize: "20px", fontWeight: 600, color: "#1c2333" },
  p: { margin: "0 0 24px", fontSize: "14px", lineHeight: 1.55, color: "#5a6473" },
  actions: { display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" },
  primaryBtn: {
    appearance: "none",
    border: "none",
    cursor: "pointer",
    background: BRAND,
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderRadius: "8px",
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    background: "#ffffff",
    color: "#1c2333",
    fontSize: "14px",
    fontWeight: 600,
    padding: "10px 20px",
    borderRadius: "8px",
    border: "1px solid #d4d9e2",
  },
  ref: { margin: "22px 0 0", fontSize: "12px", color: "#8a93a3" },
  code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#5a6473" },
  details: { marginTop: "20px", textAlign: "left" },
  summary: { cursor: "pointer", fontSize: "13px", color: BRAND, fontWeight: 600 },
  pre: {
    marginTop: "10px",
    maxHeight: "220px",
    overflow: "auto",
    background: "#0f1729",
    color: "#e2e8f0",
    fontSize: "12px",
    lineHeight: 1.5,
    padding: "12px",
    borderRadius: "8px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
};

export default ErrorBoundary;
